/**
 * Merchant kiosk config, from the kiosk config API.
 *
 * A SEPARATE slice from `kioskStore`, and the split is deliberate:
 *
 *   kioskStore        session state  — NOT persisted. A kiosk restarting mid-order must
 *                                      come back to IDLE, not resume a stranger's cart.
 *   kioskConfigStore  merchant setup — PERSISTED. The kiosk has to boot and take orders
 *                                      on a restaurant LAN with no internet, which means
 *                                      surviving a restart without reaching the API.
 *
 * redux-persist whitelists are per-slice, so this could not have been one slice with
 * half of it persisted.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { KioskConfig } from "../../../kiosk/config/types";
import type { KioskDevice } from "../../../kiosk/payment/deviceTypes";

export interface KioskConfigState {
  /**
   * Device is in kiosk mode. Staff turn it on; a customer can never turn it off.
   *
   * It lives HERE, in the persisted slice, because kiosk mode must survive a restart,
   * a crash and an OTA update — a device that reboots into the POS is a device showing
   * a customer the cashier's app. Deliberately a SIBLING of `config`, not a field
   * inside it, so a config refetch cannot clobber the mode.
   *
   * The customer's session is the opposite and lives in `kioskStore`, unpersisted: a
   * restart mid-order must return to IDLE, not resume a stranger's cart.
   */
  enabled: boolean;
  config: KioskConfig | null;
  isFetching: boolean;
  /** Timestamp of the last successful fetch — lets the UI show staleness offline. */
  fetchedAt: number | null;
  error: string | null;

  /**
   * This PANEL's own record, from `kioskdevice/fetch` — which card terminal is attached, which
   * UPI provider to show a QR for. A different API from `config`, kept in the same slice
   * because it has the same lifecycle in every respect that decided this slice's existence:
   * merchant setup, fetched per boot, and PERSISTED, because a kiosk must come back up and
   * take card payments on a restaurant LAN with no internet.
   *
   * Its own `isFetching`/`error` rather than sharing the config's: two APIs fail
   * independently, and a kiosk whose config refreshed but whose device call timed out must
   * not report itself healthy.
   */
  device: KioskDevice | null;
  isFetchingDevice: boolean;
  deviceFetchedAt: number | null;
  deviceError: string | null;
}

const initialState: KioskConfigState = {
  enabled: false,
  config: null,
  isFetching: false,
  fetchedAt: null,
  error: null,
  device: null,
  isFetchingDevice: false,
  deviceFetchedAt: null,
  deviceError: null,
};

export const kioskConfigSlice = createSlice({
  name: "kioskConfig",
  initialState,
  reducers: {
    /**
     * The ONLY way in or out of kiosk mode. Entering is a staff action; leaving is the
     * hidden exit control, never anything a customer or an error path can trigger.
     */
    setKioskEnabled: (state, action: PayloadAction<boolean>) => {
      state.enabled = action.payload;
    },
    /** Saga trigger. */
    fetchKioskConfig: (state) => {
      state.isFetching = true;
      state.error = null;
    },
    fetchKioskConfigSuccess: (
      state,
      action: PayloadAction<{ config: KioskConfig; now: number }>,
    ) => {
      state.config = action.payload.config;
      state.fetchedAt = action.payload.now;
      state.isFetching = false;
      state.error = null;
    },
    /**
     * Keeps the last-known-good config rather than clearing it. A failed refresh must
     * not take a working kiosk offline — the persisted config is the fallback.
     */
    fetchKioskConfigFailed: (state, action: PayloadAction<string>) => {
      state.isFetching = false;
      state.error = action.payload;
    },

    /** Trigger for `kioskdevice/fetch`. */
    fetchKioskDevice: (state) => {
      state.isFetchingDevice = true;
      state.deviceError = null;
    },
    fetchKioskDeviceSuccess: (
      state,
      action: PayloadAction<{ device: KioskDevice; now: number }>,
    ) => {
      console.log("==>action", action);
      state.device = action.payload.device;
      state.deviceFetchedAt = action.payload.now;
      state.isFetchingDevice = false;
      state.deviceError = null;
    },
    /**
     * Keeps the last-known-good device for the same reason the config does — and here the
     * stakes are higher. Clearing it would take a working card terminal off a panel because a
     * refresh call timed out, leaving a queue of customers with only "pay at counter".
     */
    fetchKioskDeviceFailed: (state, action: PayloadAction<string>) => {
      state.isFetchingDevice = false;
      state.deviceError = action.payload;
    },
  },
});

export const {
  setKioskEnabled,
  fetchKioskConfig,
  fetchKioskConfigSuccess,
  fetchKioskConfigFailed,
  fetchKioskDevice,
  fetchKioskDeviceSuccess,
  fetchKioskDeviceFailed,
} = kioskConfigSlice.actions;

export default kioskConfigSlice.reducer;
